const { debounce } = window.BAO.Utils

class CartItem extends window.BAO.CustomElement() {
  constructor () {
    super()

    this._loading = false
    this.sections = this.inPushCart
      ? window.BAO.sectionRenderer.pushCartSections
      : this.onCartPage
        ? window.BAO.sectionRenderer.cartSections
        : []
  }

  setupListeners () {
    super.setupListeners()

    this.changeLineItem = this.changeLineItem.bind(this)
    this.removeLineItem = this.removeLineItem.bind(this)
    this.onQuantityChange = this.onQuantityChange.bind(this)
    this.removeWishlistLineItem = this.removeWishlistLineItem.bind(this)

    this.debouncedOnQuantityChange = debounce(event => {
      this.onQuantityChange(event)
    }, 500)

    this.debouncedOnQuantityRemove = this.onQuantityChange.bind(this)

    this.listeners.add(
      this,
      window.BAO.EVENTS.QUANTITY_ADJUSTER.CHANGE,
      this.debouncedOnQuantityChange,
    )

    this.listeners.add(
      this,
      window.BAO.EVENTS.QUANTITY_ADJUSTER.REMOVE,
      this.debouncedOnQuantityRemove,
    )

    if (this.els?.remove?.exists) {
      this.listeners.add(this.els.remove.element, 'click', this.removeLineItem)
    }

    if (this.els?.variant && this.els?.variant.exists) {
      this.listeners.add(
        this.els.variant.element,
        'change',
        this.changeLineItem,
      )
    }

    if (this.els?.wishlist?.exists) {
      this.listeners.add(
        this.els.wishlist.element,
        'click',
        this.removeWishlistLineItem,
      )
    }
  }

  async removeLineItem (e, refresh = true) {
    e?.preventDefault()

    if (refresh) {
      this.loading = true
    }

    const state = await window.BAO.Cart.removeItem(this.dataset.key, {
      sections: this.mappedSections,
    })

    if (refresh) {
      this.loading = false

      if ('sections' in state) this.renderSections(state.sections)
    }
  }

  async changeLineItem (event) {
    this.loading = true

    try {
      const id = event.target.value

      const properties = ''

      let quantity = 1
      const maxQuantity =
        event?.target?.options[event?.target?.selectedIndex]?.dataset
          ?.maxInventory ?? 1
      const currentQuantity = event?.target?.dataset?.currentQuantity ?? 1
      quantity = Math.min(currentQuantity, maxQuantity)

      const sellingPlan = ''

      await this.removeLineItem(null, false)
      const state = await window.BAO.Cart.addItem(
        { id, quantity, sellingPlan, properties },
        {
          sections: this.mappedSections,
        },
      )

      if ('sections' in state) this.renderSections(state.sections)

      this.loading = false
    } catch (error) {
      console.error('Error adding variant:', error)
    }
  }

  /**
   * @param {MouseEvent} e
   *
   * @returns {void}
   */
  async removeWishlistLineItem (e) {
    e.preventDefault()

    if (e.currentTarget.classList.contains('iwishAdded')) return

    this.loading = true

    const state = await window.BAO.Cart.removeItem(
      this.dataset.id,
      this.dataset.key,
      {
        sections: this.mappedSections,
      },
    )

    this.loading = false

    if ('sections' in state) this.renderSections(state.sections)

    if (typeof Storage !== 'undefined') {
      document
        .querySelectorAll('[data-i-wishlist-external-el="count"]')
        .forEach(countEl => {
          const count =
            typeof localStorage.iWishCnt !== 'undefined'
              ? localStorage.iWishCnt
              : ''
          countEl.dataset.count = count
          countEl.innerHTML = count
        })
    }
  }

  async onQuantityChange (e) {
    if (e.detail.value === 0) {
      this.removeLineItem(e)
    } else {
      const focusedControl = this.getFocusedQuantityControl()

      const state = await window.BAO.Cart.updateQuantity(
        this.dataset.key,
        e.detail.value,
        { sections: this.mappedSections },
      )

      if ('sections' in state) {
        this.renderSections(state.sections)
        this.restoreQuantityFocus(focusedControl)
      }

      this.announceQuantityUpdate(e.detail.value)
    }
  }

  /**
   * Sections re-render after an update, which removes the focused control. Returns
   * which quantity control (minus, input or plus) of this item has focus, if any.
   *
   * @returns {string|null}
   */
  getFocusedQuantityControl () {
    const focusedEl = document.activeElement

    if (!this.contains(focusedEl)) return null

    return focusedEl.dataset.quantityAdjusterEl ?? null
  }

  /**
   * Focuses the same quantity control of this item's re-rendered replacement, so
   * keyboard and screen reader users stay in place.
   *
   * @param {string|null} control
   *
   * @returns {void}
   */
  restoreQuantityFocus (control) {
    if (!control) return

    const key = CSS.escape(this.dataset.key)

    document
      .querySelector(
        `cart-item[data-key="${key}"] [data-quantity-adjuster-el="${control}"]`,
      )
      ?.focus()
  }

  /**
   * @param {number} quantity
   *
   * @returns {void}
   */
  announceQuantityUpdate (quantity) {
    const template = this.dataset.announceQuantityUpdated
    const total = document
      .querySelector('[data-cart-total], [data-cart-page-total]')
      ?.textContent.trim()

    if (!template || !total) return

    // Assertive so it interrupts VoiceOver's announcement of the quantity field's
    // value change ("selection replaced"), which would otherwise be read first.
    window.BAO.Utils.announce(
      template
        .replace('{{ quantity }}', quantity)
        .replace('{{ total }}', total),
      { assertive: true },
    )
  }

  get mappedSections () {
    return this.sections.map(s => s.section)
  }

  get loading () {
    return this._loading
  }

  set loading (val) {
    this._loading = val

    if (this.pushCart) this.pushCart.loading = val

    this.setAttribute('aria-busy', val.toString())
  }

  get pushCart () {
    return this.closest('push-cart') || document.querySelector('push-cart')
  }

  get inPushCart () {
    return this.pushCart != null
  }

  get cart () {
    return this.closest('cart-items') || document.querySelector('cart-items')
  }

  get onCartPage () {
    return this.cart != null
  }
}

if (!customElements.get('cart-item')) {
  customElements.define('cart-item', CartItem)
}
